import React, { useCallback, useMemo } from "react";
import { useI18n } from "../hooks/i18n-hooks";
import {
    PayrollBreakdownStatus,
    ProfileType,
    TimeSheetState,
} from "../__generated__/graphql";
import { isLiftTimesheetSource } from "../utils";
import { MissionPayrollBreakdownFieldsFragment } from "../__generated__/operations/graphql";
import { RootStackParamList } from "../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import { useTheme } from "../hooks/use-theme";
import { LiftTheme } from "../theme";
import ListGroup from "./ListGroup";
import IconComposition from "./IconComposition";
import { usePayrollBreakdownFormatter } from "../hooks/payroll-beakdown-utils";
import { LabelMode } from "../ui_atoms/Label";
import { FULL_DATE_FORMAT, IN_LIST_FORMAT, useDateFormatter } from "../hooks/date";
import { addDays } from "date-fns";

const ICON_STATUE_MAP: Record<string, (theme: LiftTheme) => React.ReactNode> = {
    open: ({ colors }: LiftTheme): React.ReactNode => {
        return (
            <IconComposition
                backgroundColor={colors.warningBackground}
                iconColor={colors.onWarningBackground}
                icon="edit"
                size={13}
            />
        );
    },
    submitted: ({ colors }: LiftTheme): React.ReactNode => {
        return (
            <IconComposition
                backgroundColor={colors.successBackground}
                iconColor={colors.onSuccessBackground}
                icon="check"
                size={17}
            />
        );
    },
    waitingValidation: ({ colors }: LiftTheme): React.ReactNode => {
        return (
            <IconComposition
                backgroundColor={colors.infoBackground}
                iconColor={colors.onInfoBackground}
                icon="send"
                size={17}
            />
        );
    },
    rejected: ({ colors }: LiftTheme): React.ReactNode => {
        return (
            <IconComposition
                backgroundColor={colors.errorBackground}
                iconColor={colors.onErrorBackground}
                icon="close"
                size={17}
            />
        );
    },
};

type ScreenParams = StackScreenProps<RootStackParamList>;

type PayrollBreakdownListProps = {
    profileType: ProfileType;
    payrollBreakdowns: MissionPayrollBreakdownFieldsFragment[];
    navigation: ScreenParams["navigation"];
};

const PayrollBreakdownList: React.FC<PayrollBreakdownListProps> = ({
    profileType,
    payrollBreakdowns,
    navigation,
}) => {
    const i18n = useI18n();
    const theme = useTheme();

    const { payrollBreakdownTitle } = usePayrollBreakdownFormatter();
    const formatDate = useDateFormatter();

    const onShowPayrollBreakdown = useCallback(
        (pb: MissionPayrollBreakdownFieldsFragment) => {
            if (pb.status === PayrollBreakdownStatus.Open && profileType === ProfileType.Client) {
                navigation?.navigate("PayrollBreakdownEditor", {
                    payrollBreakdownId: pb.id || "",
                });
            } else {
                navigation?.navigate("PayrollBreakdownDetails", {
                    payrollBreakdownId: pb.id || "",
                });
            }
        },
        [navigation, profileType],
    );
    const payrollBreakdownItems = useMemo(
        () =>
            payrollBreakdowns.map((pb) => {
                const tags = [];
                let label: string | undefined = undefined;
                let iconComponent;
                const allCompleted =
                    pb.timesheets?.length > 0 &&
                    pb.timesheets?.every(
                        (ts) =>
                            ts.status === TimeSheetState.Submitted ||
                            ts.status === TimeSheetState.Accepted,
                    );
                const lastRejectedTimeSheet = pb.timesheets?.find(
                    (ts) => ts.status === TimeSheetState.Rejected,
                );
                const lastTimeSheetToDate =
                    pb.timesheets?.length > 0
                        ? pb.timesheets.reduce((latest, current) => {
                              if (!current.toDate) return latest;
                              if (!latest?.toDate) return current;
                              return new Date(current.toDate) > new Date(latest.toDate)
                                  ? current
                                  : latest;
                          })?.toDate
                        : undefined;

                if (profileType === ProfileType.Worker) {
                    if (pb.status === PayrollBreakdownStatus.Submitted) {
                        tags.push(
                            i18n.t("payroll_breakdown.submitted_info", {
                                date: pb.submittedAt
                                    ? formatDate(new Date(pb.submittedAt), IN_LIST_FORMAT)
                                    : "",
                                name: pb.missionInfo?.clientInfo?.profileName,
                            }),
                        );
                        iconComponent = ICON_STATUE_MAP["submitted"](theme);
                    } else if (pb.status === PayrollBreakdownStatus.Open) {
                        if (!isLiftTimesheetSource(pb.missionInfo.payrollSource)) {
                            iconComponent = ICON_STATUE_MAP["open"](theme);
                            tags.push(
                                i18n.t("payroll_breakdown.waiting_fill", {
                                    name: pb.missionInfo?.clientInfo?.profileName,
                                }),
                            );
                        } else if (
                            allCompleted &&
                            (!lastTimeSheetToDate || lastTimeSheetToDate >= pb.endDate)
                        ) {
                            iconComponent = ICON_STATUE_MAP["waitingValidation"](theme);
                            tags.push(
                                i18n.t("payroll_breakdown.waiting_submit", {
                                    name: pb.missionInfo?.clientInfo?.profileName,
                                }),
                            );
                        } else if (lastRejectedTimeSheet?.rejectedAt) {
                            iconComponent = ICON_STATUE_MAP["rejected"](theme);
                            tags.push(
                                i18n.t("payroll_breakdown.rejected_info", {
                                    date: formatDate(
                                        new Date(lastRejectedTimeSheet.rejectedAt),
                                        IN_LIST_FORMAT,
                                    ),
                                    name: pb.missionInfo?.clientInfo?.name,
                                }),
                            );
                            label = i18n.t("screens.payroll_breakdown_list.fix_timesheets");
                        } else {
                            iconComponent = ICON_STATUE_MAP["open"](theme);
                            label = i18n.t("screens.payroll_breakdown_list.complete_timesheets");
                        }
                    }
                }
                if (profileType === ProfileType.Client) {
                    if (pb.status === PayrollBreakdownStatus.Submitted) {
                        iconComponent = ICON_STATUE_MAP["submitted"](theme);
                        tags.push(
                            i18n.t("payroll_breakdown.submitted_info", {
                                date: pb.submittedAt
                                    ? formatDate(new Date(pb.submittedAt), FULL_DATE_FORMAT)
                                    : "",
                                name: pb.missionInfo?.clientInfo?.profileName,
                            }),
                        );
                    } else if (pb.status === PayrollBreakdownStatus.Open) {
                        iconComponent = ICON_STATUE_MAP["open"](theme);
                        if (!isLiftTimesheetSource(pb.missionInfo.payrollSource)) {
                            // we start saying that client must fill the breakdown 2 days before
                            // the end date that is the last friday
                            if (addDays(new Date(pb.endDate), -2) < new Date()) {
                                label = i18n.t(
                                    "screens.payroll_breakdown_list.fill_submit_breakdown",
                                );
                            }
                        } else {
                            if (
                                allCompleted &&
                                (!lastTimeSheetToDate || lastTimeSheetToDate >= pb.endDate)
                            ) {
                                label = i18n.t(
                                    "screens.payroll_breakdown_list.check_submit_breakdown",
                                );
                            } else {
                                tags.push(
                                    i18n.t("payroll_breakdown.open_info", {
                                        name: pb.missionInfo?.workerInfo?.name,
                                    }),
                                );
                            }
                        }
                    }
                }
                return {
                    title: payrollBreakdownTitle(pb),
                    iconComponent,
                    value: pb.id,
                    isItemsCheckable: false,
                    tags,
                    labels: [{ text: label, mode: "miniCta" as LabelMode }],
                    onPress: () => onShowPayrollBreakdown(pb),
                };
            }),
        [
            payrollBreakdowns,
            profileType,
            payrollBreakdownTitle,
            theme,
            i18n,
            formatDate,
            onShowPayrollBreakdown,
        ],
    );

    return <ListGroup items={payrollBreakdownItems} />;
};

export default PayrollBreakdownList;
